import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import sv from './locales/sv.json';

/**
 * Flatten nested translation objects into dot-separated flat keys.
 * e.g. { common: { save: "Save" } } → { "common.save": "Save" }
 *
 * This lets us disable i18next's keySeparator so that ABP flat keys
 * like "DisplayName:Order.orderNumber" are treated as literal keys
 * instead of nested paths.
 */
function flatten(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null) {
      Object.assign(result, flatten(value as Record<string, unknown>, fullKey));
    } else {
      result[fullKey] = String(value);
    }
  }
  return result;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: flatten(en) },
      sv: { translation: flatten(sv) },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'sv'] as const,
    // Disable both separators — ABP keys use colons and dots as literal
    // characters (e.g., "Menu:Sales", "DisplayName:Order.orderNumber").
    // Bundled translations are flattened above to compensate.
    nsSeparator: false,
    keySeparator: false,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // Don't auto-detect from browser - we use the user preference
      order: [],
    },
    react: {
      // Re-render components when addResourceBundle() adds new texts
      // (needed for async ABP localization loading)
      bindI18nStore: 'added',
    },
  });

export const SUPPORTED_LANGUAGES: readonly string[] = ['en', 'sv'];

export default i18n;
